import { useState, useMemo } from "react"
import {
  calculateEMI,
  generateAmortizationSchedule,
  calculatePartialPayment,
  formatCurrencyFull,
} from "@/utils/emiCalculations"
import AmortizationTable from "@/components/AmortizationTable"
import EMIChart from "@/components/EMIChart"

export default function EMICalculator() {
  const [loanAmount, setLoanAmount] = useState(2500000)
  const [interestRate, setInterestRate] = useState(8.5)
  const [tenureYears, setTenureYears] = useState(20)
  const [tenureMode, setTenureMode] = useState<"years" | "months">("years")
  const [includeGST, setIncludeGST] = useState(false)
  const [showAmortization, setShowAmortization] = useState(false)
  const [showPartialPayment, setShowPartialPayment] = useState(false)
  const [partialAmount, setPartialAmount] = useState(500000)
  const [partialMonth, setPartialMonth] = useState(12)

  const tenureMonths = tenureMode === "years" ? tenureYears * 12 : tenureYears

  const emiResult = useMemo(
    () => calculateEMI(loanAmount, interestRate, tenureMonths),
    [loanAmount, interestRate, tenureMonths]
  )

  const amortizationSchedule = useMemo(
    () => (showAmortization ? generateAmortizationSchedule(loanAmount, interestRate, tenureMonths) : []),
    [loanAmount, interestRate, tenureMonths, showAmortization]
  )

  const partialPaymentResult = useMemo(
    () =>
      showPartialPayment
        ? calculatePartialPayment(loanAmount, interestRate, tenureMonths, partialAmount, partialMonth)
        : null,
    [loanAmount, interestRate, tenureMonths, partialAmount, partialMonth, showPartialPayment]
  )

  const totalWithGST = includeGST
    ? emiResult.totalPayment + emiResult.processingFeeWithGST
    : emiResult.totalPayment

  return (
    <section id="calculator" className="py-12 md:py-16" aria-labelledby="calculator-heading">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <h1 id="calculator-heading" className="text-3xl md:text-4xl font-bold text-slate-800 mb-3">
            India's Most Accurate EMI Calculator
          </h1>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Calculate EMIs for HDFC, SBI, Axis Bank loans instantly. Get a full amortization schedule and compare bank rates.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Input Panel */}
          <div className="lg:col-span-3 bg-white rounded-2xl shadow-lg border border-slate-100 p-6 md:p-8">
            {/* Loan Amount */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="loan-amount" className="text-sm font-semibold text-slate-700">
                  Loan Amount (Principal)
                </label>
                <span className="text-sm font-bold text-blue-700">{formatCurrencyFull(loanAmount)}</span>
              </div>
              <input
                id="loan-amount"
                type="range"
                min={100000}
                max={50000000}
                step={50000}
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full"
                aria-label="Loan Amount"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>1 Lakh</span>
                <span>5 Crore</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="interest-rate" className="text-sm font-semibold text-slate-700">
                  Interest Rate (% per annum)
                </label>
                <span className="text-sm font-bold text-blue-700">{interestRate}%</span>
              </div>
              <input
                id="interest-rate"
                type="range"
                min={1}
                max={30}
                step={0.1}
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full"
                aria-label="Interest Rate"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>1%</span>
                <span>30%</span>
              </div>
            </div>

            {/* Tenure */}
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <label htmlFor="tenure" className="text-sm font-semibold text-slate-700">
                  Loan Tenure
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-blue-700">
                    {tenureYears} {tenureMode === "years" ? "Years" : "Months"}
                  </span>
                  <button
                    onClick={() => {
                      if (tenureMode === "years") {
                        setTenureMode("months")
                        setTenureYears(tenureYears * 12)
                      } else {
                        setTenureMode("years")
                        setTenureYears(Math.round(tenureYears / 12))
                      }
                    }}
                    className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded font-medium hover:bg-blue-100 transition-colors"
                  >
                    {tenureMode === "years" ? "Switch to Months" : "Switch to Years"}
                  </button>
                </div>
              </div>
              <input
                id="tenure"
                type="range"
                min={tenureMode === "years" ? 1 : 1}
                max={tenureMode === "years" ? 30 : 360}
                step={1}
                value={tenureYears}
                onChange={(e) => setTenureYears(Number(e.target.value))}
                className="w-full"
                aria-label="Loan Tenure"
              />
              <div className="flex justify-between text-xs text-slate-400 mt-1">
                <span>{tenureMode === "years" ? "1 Year" : "1 Month"}</span>
                <span>{tenureMode === "years" ? "30 Years" : "360 Months"}</span>
              </div>
            </div>

            {/* GST Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl mb-4">
              <div>
                <span className="text-sm font-semibold text-slate-700">Include GST on Processing Fee</span>
                <p className="text-xs text-slate-500 mt-0.5">18% GST on 1% processing fee</p>
              </div>
              <button
                onClick={() => setIncludeGST(!includeGST)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  includeGST ? "bg-blue-600" : "bg-slate-300"
                }`}
                role="switch"
                aria-checked={includeGST}
                aria-label="Include GST on processing fee"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    includeGST ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Partial Payment Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <span className="text-sm font-semibold text-slate-700">Partial Payment Calculator</span>
                <p className="text-xs text-slate-500 mt-0.5">See how prepayment reduces tenure</p>
              </div>
              <button
                onClick={() => setShowPartialPayment(!showPartialPayment)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  showPartialPayment ? "bg-blue-600" : "bg-slate-300"
                }`}
                role="switch"
                aria-checked={showPartialPayment}
                aria-label="Enable partial payment calculator"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    showPartialPayment ? "translate-x-6" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {/* Partial Payment Inputs */}
            {showPartialPayment ? (
              <div className="mt-4 p-4 border border-blue-100 rounded-xl bg-blue-50/50">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="partial-amount" className="text-xs font-semibold text-slate-600 block mb-1">
                      Partial Payment Amount
                    </label>
                    <input
                      id="partial-amount"
                      type="number"
                      value={partialAmount}
                      onChange={(e) => setPartialAmount(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      aria-label="Partial payment amount"
                    />
                  </div>
                  <div>
                    <label htmlFor="partial-month" className="text-xs font-semibold text-slate-600 block mb-1">
                      After Month #
                    </label>
                    <input
                      id="partial-month"
                      type="number"
                      min={1}
                      max={tenureMonths - 1}
                      value={partialMonth}
                      onChange={(e) => setPartialMonth(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                      aria-label="Partial payment month"
                    />
                  </div>
                </div>
                {partialPaymentResult ? (
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-500">New Tenure</p>
                      <p className="text-lg font-bold text-slate-800">
                        {Math.ceil(partialPaymentResult.newTenure / 12)} yrs {partialPaymentResult.newTenure % 12} mo
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Interest Saved</p>
                      <p className="text-lg font-bold text-emerald-600">
                        {formatCurrencyFull(partialPaymentResult.interestSaved)}
                      </p>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-2xl shadow-lg p-6 md:p-8 text-white">
              <h2 className="text-sm font-medium text-blue-200 mb-1">Monthly EMI</h2>
              <p className="text-3xl md:text-4xl font-bold mb-6">{formatCurrencyFull(emiResult.monthlyEMI)}</p>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-blue-200 text-sm">Principal Amount</span>
                  <span className="font-semibold">{formatCurrencyFull(loanAmount)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-blue-200 text-sm">Total Interest</span>
                  <span className="font-semibold">{formatCurrencyFull(emiResult.totalInterest)}</span>
                </div>
                {includeGST ? (
                  <div className="flex justify-between items-center">
                    <span className="text-blue-200 text-sm">Processing Fee + GST</span>
                    <span className="font-semibold">{formatCurrencyFull(emiResult.processingFeeWithGST)}</span>
                  </div>
                ) : null}
                <div className="border-t border-blue-600 pt-3 flex justify-between items-center">
                  <span className="text-blue-100 text-sm font-medium">Total Payment</span>
                  <span className="text-xl font-bold">{formatCurrencyFull(totalWithGST)}</span>
                </div>
              </div>
            </div>

            {/* Chart */}
            <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-4">Payment Breakdown</h3>
              <EMIChart principal={loanAmount} interest={emiResult.totalInterest} />
            </div>

            {/* CTA */}
            <a
              href="#eligibility"
              className="block w-full text-center bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-4 rounded-xl shadow-lg shadow-emerald-500/20 transition-all hover:shadow-emerald-500/30 hover:-translate-y-0.5"
            >
              Check Loan Eligibility
            </a>
          </div>
        </div>

        {/* Amortization Toggle */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setShowAmortization(!showAmortization)}
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 font-medium px-6 py-3 rounded-xl hover:border-blue-300 hover:text-blue-700 transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {showAmortization ? "Hide Amortization Schedule" : "View Full Amortization Schedule"}
          </button>
        </div>

        {showAmortization ? (
          <AmortizationTable schedule={amortizationSchedule} />
        ) : null}
      </div>
    </section>
  )
}
